import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth, signInWithGoogle, logOut, createUser, getUserById, getUserByEmail, updateUserUID } from '../services/firebase';
import { User } from '../types';

interface AuthContextType {
  currentUser: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async () => {
    try {
      const user = await signInWithGoogle();
      if (user && user.email) {
        setFirebaseUser(user);
        
        console.log(`🔍 Authenticating user: ${user.email} (UID: ${user.uid})`);
        
        // First, try to find user by email
        let userData = await getUserByEmail(user.email);
        
        if (!userData) {
          // No user with this email exists - create new user
          console.log('📝 Creating new user account');
          const newUserData = {
            id: user.uid,
            name: user.displayName || '',
            email: user.email,
            role: 'salesman' as const, // Default role - can be changed later
            photoURL: user.photoURL || '',
          };
          
          await createUser(newUserData);
          userData = await getUserById(user.uid);
        } else if (userData.id !== user.uid) {
          // User exists but with different UID - migrate the UID
          console.log(`🔄 Migrating user UID: ${userData.id} → ${user.uid}`);
          userData = await updateUserUID(userData.id, user.uid);
        } else {
          // User exists with same UID - all good
          console.log('✅ User found with matching UID');
        }
        
        setCurrentUser(userData as User);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logOut();
      setCurrentUser(null);
      setFirebaseUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.email) {
        setFirebaseUser(user);
        
        // Try to get user data by current UID first
        let userData = await getUserById(user.uid);
        
        if (!userData) {
          // If not found by UID, try by email (in case UID changed)
          userData = await getUserByEmail(user.email);
          
          if (userData && userData.id !== user.uid) {
            // User exists but with different UID - migrate silently
            console.log(`🔄 Silent UID migration: ${userData.id} → ${user.uid}`);
            userData = await updateUserUID(userData.id, user.uid);
          }
        }
        
        if (userData) {
          setCurrentUser(userData as User);
        }
      } else {
        setFirebaseUser(null);
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    firebaseUser,
    loading,
    login,
    logout,
    isAuthenticated: !!currentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
